/* eslint-disable sort-keys */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { ScheduleOrderingOptions, ScheduleRegisterOptions } from '../models/schedule-register-options';
import { ScheduleRegisterListItem, ScheduleRegisterResults } from '../models/schedule-register-results.model';

import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { AddScheduleComponent } from '../../add-schedule/containers/add-schedule.component';
import { createScheduleFailure, createScheduleSuccess } from '../../add-schedule/state/add-schedule.actions';
import { ModifyScheduleComponent } from '../../modify-schedule/modify-schedule.component';
import { ScheduleDetailSaveModel } from '../../schedule-details/models/schedule-detail-save.model';
import { saveScheduleDetailSuccess } from '../../schedule-details/state/schedule-detail.actions';
import { ModifyScheduleFormData } from '../models/modify-schedule-form-data';
import { SchedulesFilterOption } from '../models/schedule-filter-option';
import { GetSchedule, GetSchedule_schedulesConnection_items } from '../queries/generated/GetSchedule';
import { ScheduleRegisterApiService } from '../services/schedule-register-api.service';
import * as ScheduleRegisterActions from './schedule-register.actions';
import { createSchedule, scheduleRegisterAdapterActions, setSelectedScheduleId } from './schedule-register.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './schedule-register.reducer';
import * as ScheduleRegisterSelectors from './schedule-register.selectors';
import { selectFilters } from './schedule-register.selectors';

@Injectable()
export class ScheduleRegisterEffects {
   addSchedule$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createSchedule),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddScheduleComponent, {
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadScheduleList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            scheduleRegisterAdapterActions.pageEnter,
            ScheduleRegisterActions.refreshList,
            saveScheduleDetailSuccess
         ),
         withLatestFrom(this.store.pipe(select(selectFilters)), this.store.pipe(select(selectOrderingOptions))),
         switchMap(([, filters, orderingOptions]) => {
            return this.loadNewData(filters, null, orderingOptions);
         })
      );
   });

   loadSchedulePage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(scheduleRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(ScheduleRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, endCursor, orderingOptions, quickFilters]) => {
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(scheduleRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(scheduleRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   saveScheduleAs$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(ScheduleRegisterActions.saveScheduleAs),
         switchMap((action) => {
            const saveScheduleAsFormData = {
               schedule: { ...action.schedule },
               title: 'Save Schedule As',
            };
            return this.dialogService
               .open<ModifyScheduleComponent, ModifyScheduleFormData>(ModifyScheduleComponent, {
                  data: saveScheduleAsFormData,
                  panelClass: [DialogSize.Small, 'dialog-container'],
               })
               .afterClosed()
               .pipe(
                  switchMap((result: ScheduleDetailSaveModel) => {
                     if (result) {
                        return of(ScheduleRegisterActions.saveScheduleAsConfirmed({ schedule: result }));
                     } else {
                        return of(ScheduleRegisterActions.saveScheduleAsCanceled());
                     }
                  })
               );
         })
      );
   });

   saveScheduleAsConfirmed$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(ScheduleRegisterActions.saveScheduleAsConfirmed),
         switchMap((action) => {
            const scheduleToCopy: ScheduleDetailSaveModel = {
               ...action.schedule,
               scheduleId: 0,
            };
            return this.scheduleRegisterApiService.saveAs(scheduleToCopy).pipe(
               map((executionResult) => {
                  return createScheduleSuccess({
                     scheduleId: executionResult.data.schedule.scheduleId,
                     scheduleName: executionResult.data.schedule.name,
                  });
               }),
               catchError((error) => {
                  return of(createScheduleFailure(error));
               })
            );
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private scheduleRegisterApiService: ScheduleRegisterApiService,
      private dialogService: LcsDialogService,
      private twoColumnLayoutService: TwoColumnLayoutService
   ) {}

   private loadNewData(
      filters: SchedulesFilterOption[],
      quickFilters: ScheduleRegisterOptions | null = null,
      orderingOptions: ScheduleOrderingOptions[] | null = null
   ) {
      return this.scheduleRegisterApiService.getSchedule(null, filters, quickFilters, orderingOptions).pipe(
         switchMap((result) => {
            const convertedData = this.convertGetScheduleResultToLoadSuccessProps(result.data);
            const firstScheduleId = convertedData.items.length ? convertedData.items[0].scheduleId : null;

            this.twoColumnLayoutService.setFirstItemId(firstScheduleId);

            if (firstScheduleId !== null) {
               this.twoColumnLayoutService.setShouldShowDetailView(true);
            }

            // if there is 1+ schedules, select first one by default
            if (firstScheduleId !== null && this.twoColumnLayoutService.getSelectedId() === null) {
               this.twoColumnLayoutService.setSelectedId(firstScheduleId);
               return [
                  scheduleRegisterAdapterActions.loadSuccess(convertedData),
                  setSelectedScheduleId({ scheduleId: firstScheduleId }),
               ];
            } else {
               return [scheduleRegisterAdapterActions.loadSuccess(convertedData)];
            }
         }),
         catchError((error) => {
            return of(scheduleRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: SchedulesFilterOption[],
      quickFilters: ScheduleRegisterOptions | null = null,
      orderingOptions: ScheduleOrderingOptions[] | null = null
   ) {
      return this.scheduleRegisterApiService.getSchedule(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return scheduleRegisterAdapterActions.loadPageSuccess(
               this.convertGetScheduleResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(scheduleRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }

   private convertGetScheduleResultToLoadSuccessProps(getSchedule: GetSchedule): ScheduleRegisterResults {
      return {
         endCursor: getSchedule.schedulesConnection.pageInfo.endCursor,
         hasNextPage: getSchedule.schedulesConnection.pageInfo.hasNextPage,
         items: getSchedule.schedulesConnection.items.map(this.convertScheduleRegisterListItem.bind(this)),
         totalCount: getSchedule.schedulesConnection.totalCount,
      };
   }

   private convertScheduleRegisterListItem(
      getScheduleItem: GetSchedule_schedulesConnection_items
   ): ScheduleRegisterListItem {
      return {
         scheduleId: getScheduleItem.scheduleId || null,
         name: getScheduleItem.name || null,
         accountId: getScheduleItem.accountId,
         closedExceptions: getScheduleItem.closedExceptionsConnection.items,
         scheduleDetails: getScheduleItem.scheduleDetailsConnection.items,
         description: getScheduleItem.description || null,
         createDate: getScheduleItem.createDate,
         updateDate: getScheduleItem.updateDate,
         createUser: getScheduleItem.createUser?.name || null,
         updateUser: getScheduleItem.updateUser?.name || null,
      };
   }
}
